import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, BookOpen, Compass, Hammer, RefreshCw, Landmark, Moon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * ChapterDepth — 6 tabs (KIDS / QUICK / LEARN / DEEP DIVE / BUILD / INDIA)
 * wrapped around a KB01 chapter reader.  LEARN is always the original
 * body_md passed in as a prop (no network call).  The other five are
 * fetched from `/api/kb/chapters/:slug/depth/:level`, cached on the
 * server after first generation.
 */
const LEVELS = [
  { key: "kids",  label: "Kids",  tag: "a 120-word bedtime story", icon: Moon },
  { key: "quick", label: "Quick", tag: "90-second read", icon: Sparkles },
  { key: "learn", label: "Learn", tag: "the original chapter", icon: BookOpen },
  { key: "deep",  label: "Deep dive", tag: "for engineers", icon: Compass },
  { key: "build", label: "Build",  tag: "a hands-on project", icon: Hammer },
  { key: "india", label: "India", tag: "the same idea in Indian computing", icon: Landmark },
];

export default function ChapterDepth({ slug, learnBody }) {
  const [active, setActive] = useState("learn");
  // per-level state: { body, loading, error }
  const [cache, setCache] = useState(() => ({
    learn: { body: learnBody || "", loading: false, error: "" },
  }));

  useEffect(() => {
    // reset cache when the underlying chapter changes
    setActive("learn");
    setCache({ learn: { body: learnBody || "", loading: false, error: "" } });
  }, [slug, learnBody]);

  const fetchLevel = async (level, force = false) => {
    if (level === "learn") return;
    setCache((c) => ({ ...c, [level]: { body: force ? "" : c[level]?.body || "", loading: true, error: "" } }));
    try {
      const url = `/kb/chapters/${slug}/depth/${level}${force ? `?t=${Date.now()}` : ""}`;
      const { data } = await api.get(url);
      setCache((c) => ({ ...c, [level]: { body: data.body || "", loading: false, error: "", cached: data.cached } }));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not fetch this depth.";
      setCache((c) => ({ ...c, [level]: { body: "", loading: false, error: msg } }));
      toast.error(msg);
    }
  };

  const onTabClick = (level) => {
    setActive(level);
    if (level === "learn") return;
    const entry = cache[level];
    if (!entry || (!entry.body && !entry.loading)) fetchLevel(level);
  };

  const current = cache[active] || { body: "", loading: false };

  return (
    <div data-testid={`chapter-depth-${slug}`}>
      {/* Tabs — pop-up-book tab strip along the top */}
      <div className="mb-6 border-b-2 border-ink flex flex-wrap items-end gap-1" data-testid="chapter-depth-tabs">
        {LEVELS.map((l) => {
          const Icon = l.icon;
          const isActive = active === l.key;
          return (
            <button
              key={l.key}
              onClick={() => onTabClick(l.key)}
              data-testid={`chapter-depth-tab-${l.key}`}
              className={`relative px-4 md:px-5 py-2.5 -mb-[2px] border-2 border-ink rounded-t-2xl text-xs md:text-sm font-bold uppercase tracking-[0.16em] inline-flex items-center gap-2 transition ${
                isActive
                  ? "bg-cream text-ink border-b-cream shadow-[3px_-3px_0_0_#1F1A17]"
                  : "bg-ink/[0.04] text-ink2 border-b-ink hover:bg-marigold/10 hover:text-marigold"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${isActive ? "text-marigold" : ""}`} />
              {l.label}
              {isActive && <span className="hidden md:inline text-[9px] font-normal tracking-normal text-ink2 italic ml-1">— {l.tag}</span>}
            </button>
          );
        })}
        <div className="ml-auto text-[10px] uppercase tracking-[0.28em] font-bold text-ink2/60 hidden md:block pb-3 pr-2">
          One chapter, six ways to read it
        </div>
      </div>

      {/* Body */}
      {current.loading && (
        <div className="py-10 text-center text-ink2" data-testid={`chapter-depth-loading-${active}`}>
          <Loader2 className="w-6 h-6 mx-auto animate-spin mb-3 text-marigold" />
          <div className="text-sm">
            {active === "quick" && "Ms. Pradhan is trimming this down to a 90-second read…"}
            {active === "deep"  && "Ms. Pradhan is writing the engineer's take…"}
            {active === "build" && "Ms. Pradhan is sketching the build challenge…"}
            {active === "india" && "Ms. Pradhan is finding this idea's Indian twin…"}
            {active === "kids"  && "Ms. Pradhan is telling this as a bedtime story…"}
          </div>
          <div className="text-xs text-ink2/60 mt-1 italic">(takes ~15 seconds the first time; instant after that)</div>
        </div>
      )}

      {!current.loading && current.error && (
        <div className="rounded-2xl border-2 border-marigold/40 bg-marigold/10 p-4 text-sm text-ink" data-testid={`chapter-depth-error-${active}`}>
          {current.error}
          <button onClick={() => fetchLevel(active, true)} className="ml-2 underline text-marigold">Try again</button>
        </div>
      )}

      {!current.loading && !current.error && current.body && (
        <article
          className="prose prose-slate max-w-none prose-headings:font-heading prose-headings:text-ink prose-a:text-marigold"
          data-testid={`chapter-depth-body-${active}`}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.body}</ReactMarkdown>
          {active !== "learn" && (
            <div className="not-prose mt-8 flex items-center justify-between gap-3 flex-wrap pt-4 border-t border-ink2/20">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-ink2/60">
                Written by Claude Sonnet 4.5 · from Ms. Pradhan's chapter
              </div>
              <button
                onClick={() => fetchLevel(active, true)}
                className="text-xs inline-flex items-center gap-1 text-ink2 hover:text-marigold"
                data-testid={`chapter-depth-regen-${active}`}
              >
                <RefreshCw className="w-3.5 h-3.5" /> Regenerate
              </button>
            </div>
          )}
        </article>
      )}

      {!current.loading && !current.error && !current.body && active === "learn" && (
        <div className="card-soft p-6 text-ink2 text-sm">This chapter has no body content.</div>
      )}
    </div>
  );
}
